'use client';

import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface EditableContentProps {
    value: string; // The initial value
    onSave: (newValue: string) => Promise<void>;
    role?: 'input' | 'textarea';
    canEdit: boolean;
    className?: string; // Class for the view mode container (and input/textarea)
    children?: React.ReactNode; // The view mode content (if intricate rendering is needed, else value is used)
    placeholder?: string;
    multiline?: boolean; // If true, uses textarea
}

export function EditableContent({
    value,
    onSave,
    canEdit,
    className,
    children,
    placeholder,
    multiline = false
}: EditableContentProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            if (inputRef.current.tagName === 'TEXTAREA') {
                const el = inputRef.current as HTMLTextAreaElement;
                el.style.height = 'auto';
                el.style.height = `${el.scrollHeight}px`;
            }
        }
    }, [isEditing]);

    useEffect(() => {
        if (!isEditing) return;

        const handleClickOutside = (e: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
                handleSave();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isEditing, currentValue, value]);

    const handleSave = async () => {
        if (currentValue === value) {
            setIsEditing(false);
            return;
        }

        try {
            setIsSaving(true);
            await onSave(currentValue);
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to save:", error);
            // Optionally show error toast
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setCurrentValue(value);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleCancel();
        }
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSave();
        }
    };

    if (!canEdit) {
        return <div className={className}>{children || value}</div>;
    }

    if (isEditing) {
        return (
            <div className="relative group">
                {multiline ? (
                    <textarea
                        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                        value={currentValue}
                        onChange={(e) => {
                            setCurrentValue(e.target.value);
                            e.target.style.height = 'auto';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                        }}
                        onKeyDown={handleKeyDown}
                        className={cn(
                            "w-full p-0 border-none outline-none bg-transparent text-inherit resize-none whitespace-pre-wrap overflow-hidden",
                            className
                        )}
                        placeholder={placeholder}
                        spellCheck={false}
                    />
                ) : (
                    <input
                        ref={inputRef as React.RefObject<HTMLInputElement>}
                        value={currentValue}
                        onChange={(e) => setCurrentValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={cn(
                            "w-full p-0 border-none outline-none bg-transparent text-inherit",
                            className
                        )}
                        placeholder={placeholder}
                        spellCheck={false}
                    />
                )}
            </div>
        );
    }

    return (
        <div
            onClick={() => setIsEditing(true)}
            className={cn(
                "cursor-text",
                className
            )}
            title="Click to edit"
        >
            {children || value}
        </div>
    );
}
